import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../data";
import { PublicLayout } from "../../components/shell/WebLayout";
import { kitLangVorab } from "./kit-model";

/** Stand des Links der erziehungsberechtigten Person (0518, public.guardian_consent_view) */
export interface EinwilligungStand {
  state: "open" | "confirmed" | "declined" | "expired" | "revoked" | "unknown";
  creator?: string | null;
  agency?: string | null;
  guardian_name?: string | null;
  language?: "de" | "en" | null;
  expires_at?: string | null;
}

export async function ladeEinwilligung(token: string): Promise<EinwilligungStand> {
  if (token.trim().length < 20) return { state: "unknown" };
  return api.rpcAsAnon<EinwilligungStand>("guardian_consent_view", { p_token: token.trim() });
}
export async function entscheideEinwilligung(token: string, zustimmen: boolean): Promise<"confirmed" | "declined"> {
  const r = await api.rpcAsAnon<{ state: "confirmed" | "declined" }>("guardian_consent_decide", { p_token: token.trim(), p_agree: zustimmen });
  return r.state;
}

/**
 * Öffentliche Seite für Erziehungsberechtigte (0518, Befund B6): /einwilligung?t=… aus der Mail email.guardian_confirm. Ohne
 * Anmeldung; sie zeigt, für wen und bei welcher Agentur eingewilligt wird, und nimmt Einwilligung oder Ablehnung an. Erst danach
 * kann der Creator die Einladung annehmen. Sprache nach ?sprache=en, der Profilsprache oder dem Browser, umschaltbar.
 */
export function Einwilligung() {
  const [params] = useSearchParams();
  const token = params.get("t") ?? "";
  const [lang, setLang] = useState<"de" | "en">(() => (params.get("sprache") === "en" ? "en" : kitLangVorab(`?${params.toString()}`, typeof navigator !== "undefined" ? navigator.language : undefined)));
  const [stand, setStand] = useState<EinwilligungStand | null>(null);
  const [busy, setBusy] = useState<"" | "ja" | "nein">("");
  const [fehler, setFehler] = useState<string | null>(null);
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const datum = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "de-DE", { dateStyle: "long" }) : "");

  useEffect(() => {
    let alive = true;
    void ladeEinwilligung(token).then((s) => alive && setStand(s)).catch(() => alive && setStand({ state: "unknown" }));
    return () => { alive = false; };
  }, [token]);

  async function entscheiden(zustimmen: boolean) {
    setBusy(zustimmen ? "ja" : "nein");
    setFehler(null);
    try {
      const state = await entscheideEinwilligung(token, zustimmen);
      setStand((s) => ({ ...(s ?? {}), state }));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t("Das hat gerade nicht geklappt. Bitte versuchen Sie es gleich noch einmal.", "That did not work just now. Please try again shortly."));
    } finally {
      setBusy("");
    }
  }

  const wer = stand?.creator ?? t("Ihr Kind", "your child");
  const agentur = stand?.agency ?? t("die Agentur", "the agency");
  return (
    <PublicLayout lang={lang} themeLabel={t("Hell / Dunkel", "Light / dark")}>
      <title>{t("Einwilligung · CreatorOffice", "Consent · CreatorOffice")}</title>
      <main id="main" className="auth-shell stack">
        <section className="centerform panel stack" aria-labelledby="einwilligung-titel">
          <div className="row between">
            <div className="eyebrow">{t("Einwilligung", "Consent")}</div>
            <button type="button" onClick={() => setLang(lang === "en" ? "de" : "en")} lang={lang === "en" ? "de" : "en"}>{lang === "en" ? "Deutsch" : "English"}</button>
          </div>
          <h1 id="einwilligung-titel">{t("Einwilligung als erziehungs\u00ADberechtigte Person", "Consent as parent or guardian")}</h1>
          {!stand && <p role="status">{t("Wird geladen …", "Loading …")}</p>}
          {stand?.state === "open" && (
            <div className="stack">
              <p>{t(`${wer} möchte im Creator-Portal von ${agentur} mitarbeiten und hat Sie als erziehungsberechtigte Person angegeben. Der Zugang wird erst aktiv, wenn Sie einwilligen.`,
                `${wer} would like to work in the creator portal of ${agentur} and has named you as parent or guardian. Access only becomes active once you consent.`)}</p>
              <p className="muted">{t(`Verträge schickt Ihnen die Agentur zur eigenen Unterschrift an Ihre Adresse. Der Link gilt bis ${datum(stand.expires_at)}.`,
                `The agency will send contracts to your address for your own signature. The link is valid until ${datum(stand.expires_at)}.`)}</p>
              {fehler && <p className="tk-error" role="alert">{fehler}</p>}
              <div className="row">
                <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "ja"} onClick={() => void entscheiden(true)}>
                  {busy === "ja" ? t("Wird gespeichert …", "Saving …") : t("Ich willige ein", "I consent")}
                </button>
                <button type="button" disabled={!!busy} aria-busy={busy === "nein"} onClick={() => void entscheiden(false)}>
                  {busy === "nein" ? t("Wird gespeichert …", "Saving …") : t("Ich willige nicht ein", "I do not consent")}
                </button>
              </div>
            </div>
          )}
          {stand?.state === "confirmed" && (
            <p role="status">{t(`Einwilligung gespeichert. ${wer} bekommt eine Mail und kann die Einladung von ${agentur} jetzt annehmen.`,
              `Consent saved. ${wer} gets an email and can now accept the invitation from ${agentur}.`)}</p>
          )}
          {stand?.state === "declined" && (
            <p role="status">{t(`Ablehnung gespeichert. Der Zugang von ${wer} bleibt inaktiv; ${wer} bekommt eine Mail. Fragen beantwortet ${agentur}.`,
              `Refusal saved. Access for ${wer} stays inactive; ${wer} gets an email. ${agentur} answers any questions.`)}</p>
          )}
          {(stand?.state === "expired" || stand?.state === "revoked") && (
            <p role="alert">{t("Dieser Link gilt nicht mehr. Die Agentur kann eine neue Einladung schicken; danach kommt ein neuer Link.", "This link is no longer valid. The agency can send a new invitation; a new link follows.")}</p>
          )}
          {stand?.state === "unknown" && (
            <p role="alert">{t("Zu diesem Link gibt es keine Einwilligung. Bitte nutzen Sie den vollständigen Link aus der Mail.", "There is no consent for this link. Please use the complete link from the email.")}</p>
          )}
        </section>
      </main>
    </PublicLayout>
  );
}
